import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TIP, cn } from "./ui";

// One tooltip for the whole app. Any element with a `title` attribute gets this styled tooltip
// instead of the browser's grey one: on hover / keyboard focus the title is moved aside (so the
// native tooltip never shows) and put back on leave (so React's own title updates keep working).
// Icon-only elements also get the text as aria-label, so screen readers lose nothing.
// Conventions: "\n" in a title starts a new line; a trailing "[Ctrl K]" renders as a key hint.

const SHOW_DELAY = 400; // ms before the first tooltip
const WARM_DELAY = 60; // ms while moving between elements that have tooltips
const WARM_WINDOW = 500; // ms after hiding during which the next tooltip is "warm"

const GAP = 8; // px between the element and its tooltip

// `edge` = bottom of the sticky app header: tooltips must stay below it, never ride up over it
type Tip = { text: string; kbd?: string; rect: DOMRect; edge: number; inHeader: boolean };

export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let current: Element | null = null;
    let stashed: string | null = null;
    let timer = 0;
    let lastHide = 0;
    let observer: MutationObserver | null = null;

    const parse = (raw: string) => {
      const m = /\s*\[([^\]]+)\]\s*$/.exec(raw);
      return m ? { text: raw.slice(0, m.index).trim(), kbd: m[1] } : { text: raw.trim() };
    };

    const restore = () => {
      observer?.disconnect();
      observer = null;
      if (current && stashed != null && current.isConnected && !current.hasAttribute("title")) {
        current.setAttribute("title", stashed);
      }
      current = null;
      stashed = null;
    };

    const hide = () => {
      window.clearTimeout(timer);
      if (current) lastHide = Date.now();
      restore();
      setTip(null);
    };

    const show = (el: Element) => {
      const raw = el.getAttribute("title");
      if (!raw || !raw.trim()) return;
      window.clearTimeout(timer);
      restore();
      current = el;
      stashed = raw;
      el.removeAttribute("title");
      if (!el.getAttribute("aria-label") && !(el.textContent ?? "").trim()) el.setAttribute("aria-label", raw);
      // React may re-set the title while we're showing it (e.g. a toggle that changes its label):
      // take the new value over and keep the tooltip text in sync.
      observer = new MutationObserver(() => {
        const next = el.getAttribute("title");
        if (next == null) return;
        stashed = next;
        el.removeAttribute("title");
        setTip((t) => (t ? { ...t, ...parse(next) } : t));
      });
      observer.observe(el, { attributes: true, attributeFilter: ["title"] });
      const delay = Date.now() - lastHide < WARM_WINDOW ? WARM_DELAY : SHOW_DELAY;
      timer = window.setTimeout(() => {
        if (current !== el || !el.isConnected) return;
        const header = document.querySelector("header");
        const hr = header?.getBoundingClientRect();
        setTip({
          ...parse(raw),
          rect: el.getBoundingClientRect(),
          edge: hr && hr.height > 0 ? hr.bottom : 0,
          inHeader: !!header?.contains(el),
        });
      }, delay);
    };

    const onOver = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      const el = (e.target as Element | null)?.closest?.("[title]");
      if (el && el !== current) show(el);
    };
    const onOut = (e: PointerEvent) => {
      if (!current) return;
      const to = e.relatedTarget as Node | null;
      if (to && current.contains(to)) return;
      hide();
    };
    const onFocus = (e: FocusEvent) => {
      const el = e.target as Element;
      if (el instanceof HTMLElement && el.matches(":focus-visible") && el.hasAttribute("title")) show(el);
    };

    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("pointerout", onOut, true);
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("focusout", hide, true);
    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("keydown", hide, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      hide();
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("pointerout", onOut, true);
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("focusout", hide, true);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("keydown", hide, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  // place above the element (below if there's no room), centred, kept inside the viewport.
  // The sticky header counts as "no room": a tip above an element just under it used to stick to
  // the very top, over the header; header tips drop clear of its bottom border instead of hugging it.
  useEffect(() => {
    if (!tip || !boxRef.current) {
      setPos(null);
      return;
    }
    const b = boxRef.current.getBoundingClientRect();
    const r = tip.rect;
    const below = tip.inHeader || r.top - b.height - GAP < Math.max(4, tip.edge + 4);
    const top = tip.inHeader
      ? Math.max(r.bottom + GAP, tip.edge + 6)
      : below
        ? r.bottom + GAP
        : r.top - b.height - GAP;
    const left = Math.max(8, Math.min(window.innerWidth - b.width - 8, r.left + r.width / 2 - b.width / 2));
    setPos({ left, top, below });
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div
      ref={boxRef}
      role="tooltip"
      className={cn("pointer-events-none fixed z-[200] w-max max-w-[300px] print:hidden", TIP)}
      style={pos ? { left: pos.left, top: pos.top, animation: "fade-in .12s ease-out both" } : { left: -9999, top: -9999 }}
    >
      {/* multi-line tips: the first line reads as the heading */}
      {tip.text.split("\n").map((line, i, all) => (
        <span key={i} className={cn(all.length > 1 ? "block" : "inline", i === 0 && all.length > 1 && "mb-0.5 font-semibold")}>
          {line}
        </span>
      ))}
      {tip.kbd && (
        <kbd
          className={cn(
            "rounded border border-bg/25 px-1 py-px font-sans text-2xs text-bg/80",
            tip.text.includes("\n") ? "mt-1 block w-fit" : "ml-2 inline-block align-[1px]",
          )}
        >
          {tip.kbd}
        </kbd>
      )}
    </div>,
    document.body,
  );
}
